// client/src/components/promoters/PromotersPage.jsx
//
// /:tenant/promoters — recruit promoters (staff, volunteers, ambassadors,
// street team), give each a personal tracked link per event, invite them to
// their member-portal page, and approve / pay their commission.
// Growth Phase 2, feature_key "promoters".
//
// API: /api/promoters/:tenant/promoters[...], /api/promoters/:tenant/earnings

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { delJson, getJson, patchJson, postJson } from '../../services/http';
import { formatCount, formatMoney } from '../analytics/attributionFormat';
import '../analytics/PerformancePanel.css';
import './Promoters.css';

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';
const EMPTY_FORM = {
  name: '', email: '', phone: '', code: '', defaultCommissionType: 'none', defaultCommissionValue: '', sendInvite: true,
};

export function commissionText(t, type, value) {
  if (type === 'percent') return t('promoters.commissionPercent', '{{value}}% of each sale', { value: Number(value) || 0 });
  if (type === 'flat') return t('promoters.commissionFlat', '{{value}} per conversion', { value: formatMoney(value) });
  return t('promoters.commissionNone', 'No commission');
}

export default function PromotersPage({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const [tab, setTab] = useState('promoters');

  const navButtons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
  ];

  return (
    <div className="pr-page">
      <DesktopActionBar buttons={navButtons} />
      <section className="surface">
        <h2>{t('promoters.title', 'Promoters')}</h2>
        <p className="surface-muted perf-small">
          {t('promoters.intro', 'Give staff, volunteers and ambassadors their own tracked link for each event. Clicks, sales and commission are counted per promoter.')}
        </p>
      </section>
      <div className="pr-tabs" role="tablist">
        {['promoters', 'earnings'].map((k) => (
          <button key={k} type="button" role="tab" className="pr-tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {k === 'promoters' ? t('promoters.tabPromoters', 'Promoters') : t('promoters.tabEarnings', 'Earnings')}
          </button>
        ))}
      </div>
      {tab === 'promoters' ? <PromoterList tenant={tenant} /> : <EarningsPanel tenant={tenant} />}
      <AppFooter buttons={navButtons} />
    </div>
  );
}

function PromoterList({ tenant }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await getJson(`/api/promoters/${tenant}/promoters${status ? `?status=${status}` : ''}`);
      setRows(res.data || []);
    } catch (err) {
      setError(/^409/.test(err.message)
        ? t('promoters.notReady', 'Promoters need a database update before they can be used.')
        : t('promoters.loadError', 'Could not load promoters.'));
    }
  }, [tenant, status, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getJson(`/api/events/${tenant}/events`)
      .then((d) => setEvents(d.data || d.events || []))
      .catch(() => setEvents([]));
  }, [tenant]);

  const create = async (e) => {
    e.preventDefault();
    setNotice(null);
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        code: form.code.trim() || undefined,
        defaultCommissionType: form.defaultCommissionType,
        defaultCommissionValue: form.defaultCommissionType === 'none' ? 0 : Number(form.defaultCommissionValue) || 0,
        sendInvite: !!(form.sendInvite && form.email.trim()),
      };
      const res = await postJson(`/api/promoters/${tenant}/promoters`, body);
      setForm(EMPTY_FORM);
      setNotice({
        kind: 'ok',
        text: body.sendInvite
          ? t('promoters.createdInvited', 'Added {{name}} ({{code}}) and emailed an invite.', { name: res.data.name, code: res.data.code })
          : t('promoters.created', 'Added {{name}} ({{code}}).', { name: res.data.name, code: res.data.code }),
      });
      setOpenId(res.data.id);
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('promoters.createError', 'Could not add the promoter.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className="surface">
        <h4>{t('promoters.add', 'Add a promoter')}</h4>
        <form className="lm-form" onSubmit={create}>
          <label>
            {t('promoters.name', 'Name')}
            <input type="text" required maxLength={120} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </label>
          <label>
            {t('promoters.email', 'Email')}
            <input type="email" maxLength={100} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </label>
          <label>
            {t('promoters.phone', 'Phone')}
            <input type="tel" maxLength={50} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </label>
          <label>
            {t('promoters.code', 'Code (optional)')}
            <input
              type="text" maxLength={32} pattern="[A-Za-z0-9_\-]{3,32}"
              placeholder={t('promoters.codePlaceholder', 'JANE')}
              value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            />
          </label>
          <label>
            {t('promoters.commission', 'Commission')}
            <select value={form.defaultCommissionType} onChange={(e) => setForm((f) => ({ ...f, defaultCommissionType: e.target.value }))}>
              <option value="none">{t('promoters.typeNone', 'None')}</option>
              <option value="percent">{t('promoters.typePercent', 'Percent of sale')}</option>
              <option value="flat">{t('promoters.typeFlat', 'Flat per conversion')}</option>
            </select>
          </label>
          {form.defaultCommissionType !== 'none' && (
            <label>
              {form.defaultCommissionType === 'percent' ? t('promoters.percent', 'Percent') : t('promoters.amount', 'Amount')}
              <input
                type="number" min="0" step="0.01" max={form.defaultCommissionType === 'percent' ? 100 : undefined} required
                value={form.defaultCommissionValue} onChange={(e) => setForm((f) => ({ ...f, defaultCommissionValue: e.target.value }))}
              />
            </label>
          )}
          <label className="pr-check">
            <input type="checkbox" checked={form.sendInvite} onChange={(e) => setForm((f) => ({ ...f, sendInvite: e.target.checked }))} />
            {t('promoters.sendInvite', 'Email an invite')}
          </label>
          <button type="submit" className="lm-btn" disabled={saving}>
            {saving ? t('common.loading', 'Loading...') : t('promoters.addButton', 'Add promoter')}
          </button>
        </form>
      </section>

      {notice && <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}

      <section className="surface">
        <div className="perf-head">
          <h4>{t('promoters.list', 'Your promoters')}</h4>
          <select className="lm-filter" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('promoters.filterStatus', 'Filter by status')}>
            <option value="">{t('promoters.statusCurrent', 'Active and paused')}</option>
            <option value="active">{t('promoters.status.active', 'Active')}</option>
            <option value="paused">{t('promoters.status.paused', 'Paused')}</option>
            <option value="archived">{t('promoters.status.archived', 'Archived')}</option>
          </select>
        </div>
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : rows === null ? (
          <LoadingState inline />
        ) : rows.length === 0 ? (
          <div className="note info">{t('promoters.empty', 'No promoters yet. Add one above.')}</div>
        ) : (
          <div className="perf-table-wrap">
            <table className="perf-table">
              <thead>
                <tr>
                  <th>{t('promoters.promoter', 'Promoter')}</th>
                  <th className="num">{t('promoters.events', 'Events')}</th>
                  <th className="num">{t('performance.clicks', 'Clicks')}</th>
                  <th className="num">{t('performance.conversions', 'Conversions')}</th>
                  <th className="num">{t('performance.revenue', 'Revenue')}</th>
                  <th className="num">{t('promoters.owed', 'Owed')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr>
                      <td>
                        <strong>{p.name}</strong> <span className="lm-badge">{p.code}</span>
                        {p.status !== 'active' && <span className="lm-badge off">{t(`promoters.status.${p.status}`, p.status)}</span>}
                        <div className="lm-dest">{commissionText(t, p.defaultCommissionType, p.defaultCommissionValue)}</div>
                      </td>
                      <td className="num">{formatCount(p.summary.assignments)}</td>
                      <td className="num">{formatCount(p.summary.clicks)}</td>
                      <td className="num">{formatCount(p.summary.conversions)}</td>
                      <td className="num">{formatMoney(p.summary.revenue)}</td>
                      <td className="num">{formatMoney((p.earnings.pending || 0) + (p.earnings.approved || 0))}</td>
                      <td>
                        <button type="button" className="lm-btn secondary" aria-expanded={openId === p.id} onClick={() => setOpenId(openId === p.id ? null : p.id)}>
                          {openId === p.id ? t('promoters.close', 'Close') : t('promoters.manage', 'Manage')}
                        </button>
                      </td>
                    </tr>
                    {openId === p.id && (
                      <tr className="pr-row-detail">
                        <td colSpan={7}>
                          <PromoterDetail tenant={tenant} promoterId={p.id} events={events} onChanged={load} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export function PromoterDetail({ tenant, promoterId, events, onChanged }) {
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [assign, setAssign] = useState({ eventId: '', commissionType: '', commissionValue: '', goalCount: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getJson(`/api/promoters/${tenant}/promoters/${promoterId}`);
      setP(res.data);
    } catch {
      setError(t('promoters.loadError', 'Could not load promoters.'));
    }
  }, [tenant, promoterId, t]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okText) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      if (okText) setNotice({ kind: 'ok', text: okText });
      await load();
      onChanged && onChanged();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('promoters.actionError', 'That did not work. Try again.') });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice({ kind: 'ok', text: t('links.copied', 'Copied {{url}}', { url }) });
    } catch {
      setNotice({ kind: 'error', text: t('links.copyError', 'Copy failed. Select the link and copy it manually.') });
    }
  };

  if (error) return <div className="note error">{error}</div>;
  if (!p) return <LoadingState inline />;
  const live = p.assignments.filter((a) => !a.removed);

  return (
    <div className="pr-detail-grid">
      <div className="pr-inline">
        <span className="surface-muted">{p.email || t('promoters.noEmail', 'No email')}</span>
        <span className="lm-badge">{p.memberLinked ? t('promoters.joined', 'Signed in') : p.invitedOn ? t('promoters.invited', 'Invited') : t('promoters.notInvited', 'Not invited')}</span>
        {p.email && p.status !== 'archived' && (
          <button type="button" className="lm-btn secondary" disabled={busy}
            onClick={() => run(() => postJson(`/api/promoters/${tenant}/promoters/${p.id}/invite`, {}), t('promoters.inviteSent', 'Invite sent to {{email}}.', { email: p.email }))}>
            {p.invitedOn ? t('promoters.resendInvite', 'Resend invite') : t('promoters.sendInviteNow', 'Send invite')}
          </button>
        )}
        <label className="pr-check">
          {t('promoters.statusLabel', 'Status')}
          <select value={p.status} disabled={busy}
            onChange={(e) => run(() => patchJson(`/api/promoters/${tenant}/promoters/${p.id}`, { status: e.target.value }))}>
            <option value="active">{t('promoters.status.active', 'Active')}</option>
            <option value="paused">{t('promoters.status.paused', 'Paused')}</option>
            <option value="archived">{t('promoters.status.archived', 'Archived')}</option>
          </select>
        </label>
      </div>

      {notice && <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}

      <div className="surface">
        <h4>{t('promoters.assignments', 'Events and links')}</h4>
        {live.length === 0 ? (
          <p className="surface-muted perf-small">{t('promoters.noAssignments', 'Not assigned to any event yet.')}</p>
        ) : (
          <div className="perf-table-wrap">
            <table className="perf-table">
              <thead>
                <tr>
                  <th>{t('promoters.event', 'Event')}</th>
                  <th className="num">{t('performance.clicks', 'Clicks')}</th>
                  <th className="num">{t('performance.conversions', 'Conversions')}</th>
                  <th className="num">{t('promoters.goal', 'Goal')}</th>
                  <th>{t('links.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {live.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.eventName}{a.itemName ? ` · ${a.itemName}` : ''}</strong>
                      {a.trackingUrl && <div className="lm-url">{a.trackingUrl}</div>}
                      <div className="lm-dest">
                        {a.commissionType ? commissionText(t, a.commissionType, a.commissionValue) : t('promoters.usesDefault', 'Default commission')}
                      </div>
                    </td>
                    <td className="num">{formatCount(a.stats.clicks)}</td>
                    <td className="num">{formatCount(a.stats.conversions)}</td>
                    <td className="num">{a.goalCount ? formatCount(a.goalCount) : '–'}</td>
                    <td>
                      <div className="lm-actions">
                        {a.trackingUrl && <button type="button" className="lm-btn secondary" onClick={() => copy(a.trackingUrl)}>{t('links.copy', 'Copy')}</button>}
                        <button type="button" className="lm-btn secondary" disabled={busy}
                          onClick={() => run(() => delJson(`/api/promoters/${tenant}/promoters/${p.id}/assignments/${a.id}`), t('promoters.unassigned', 'Removed from {{event}}. The link still works but earns nothing new.', { event: a.eventName }))}>
                          {t('promoters.unassign', 'Remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {p.status !== 'archived' && (
          <form
            className="lm-form" style={{ marginTop: 10 }}
            onSubmit={(e) => {
              e.preventDefault();
              const body = { eventId: Number(assign.eventId) };
              if (assign.commissionType) { body.commissionType = assign.commissionType; body.commissionValue = Number(assign.commissionValue) || 0; }
              if (assign.goalCount) body.goalCount = Number(assign.goalCount);
              run(async () => {
                await postJson(`/api/promoters/${tenant}/promoters/${p.id}/assignments`, body);
                setAssign({ eventId: '', commissionType: '', commissionValue: '', goalCount: '' });
              }, t('promoters.assigned', 'Assigned. Their link is ready.'));
            }}
          >
            <label>
              {t('promoters.assignTo', 'Assign to event')}
              <select required value={assign.eventId} onChange={(e) => setAssign((s) => ({ ...s, eventId: e.target.value }))}>
                <option value="">{t('promoters.pickEvent', 'Choose an event')}</option>
                {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>
            </label>
            <label>
              {t('promoters.commission', 'Commission')}
              <select value={assign.commissionType} onChange={(e) => setAssign((s) => ({ ...s, commissionType: e.target.value }))}>
                <option value="">{t('promoters.usesDefault', 'Default commission')}</option>
                <option value="none">{t('promoters.typeNone', 'None')}</option>
                <option value="percent">{t('promoters.typePercent', 'Percent of sale')}</option>
                <option value="flat">{t('promoters.typeFlat', 'Flat per conversion')}</option>
              </select>
            </label>
            {(assign.commissionType === 'percent' || assign.commissionType === 'flat') && (
              <label>
                {assign.commissionType === 'percent' ? t('promoters.percent', 'Percent') : t('promoters.amount', 'Amount')}
                <input type="number" min="0" step="0.01" required value={assign.commissionValue}
                  onChange={(e) => setAssign((s) => ({ ...s, commissionValue: e.target.value }))} />
              </label>
            )}
            <label>
              {t('promoters.goalOptional', 'Goal (optional)')}
              <input type="number" min="1" step="1" value={assign.goalCount} onChange={(e) => setAssign((s) => ({ ...s, goalCount: e.target.value }))} />
            </label>
            <button type="submit" className="lm-btn" disabled={busy}>{t('promoters.assign', 'Assign')}</button>
          </form>
        )}
      </div>
    </div>
  );
}

function EarningsPanel({ tenant }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('pending');
  const [selected, setSelected] = useState([]);
  const [payoutRef, setPayoutRef] = useState('');
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await getJson(`/api/promoters/${tenant}/earnings${status ? `?status=${status}` : ''}`);
      setRows(res.data || []);
      setSelected([]);
    } catch {
      setError(t('promoters.earningsError', 'Could not load earnings.'));
    }
  }, [tenant, status, t]);
  useEffect(() => { load(); }, [load]);

  const update = async (next) => {
    setNotice(null);
    try {
      const res = await patchJson(`/api/promoters/${tenant}/earnings`, { ids: selected, status: next, payoutReference: payoutRef.trim() || undefined });
      setNotice({ kind: 'ok', text: t('promoters.earningsUpdated', '{{count}} updated.', { count: res.updated }) });
      setPayoutRef('');
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('promoters.actionError', 'That did not work. Try again.') });
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/promoters/${tenant}/earnings?format=csv${status ? `&status=${status}` : ''}`, { credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promoter-earnings-${status || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setNotice({ kind: 'error', text: t('promoters.exportError', 'Could not export the CSV.') });
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const actionable = (rows || []).filter((r) => r.status === 'pending' || r.status === 'approved');

  return (
    <section className="surface">
      <div className="perf-head">
        <h4>{t('promoters.earnings', 'Earnings')}</h4>
        <div className="pr-inline">
          <select className="lm-filter" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('promoters.filterStatus', 'Filter by status')}>
            <option value="">{t('promoters.all', 'All')}</option>
            {['pending', 'approved', 'paid', 'void'].map((s) => <option key={s} value={s}>{t(`promoters.earningStatus.${s}`, s)}</option>)}
          </select>
          <button type="button" className="lm-btn secondary" onClick={exportCsv}>{t('promoters.exportCsv', 'Export CSV')}</button>
        </div>
      </div>
      <p className="surface-muted perf-small">
        {t('promoters.earningsIntro', 'Commission is created when a sale is credited to a promoter link and voided if the order is refunded. Pay promoters yourself, then mark the earnings paid.')}
      </p>
      {notice && <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}
      {selected.length > 0 && (
        <div className="pr-inline" style={{ margin: '8px 0' }}>
          <span className="surface-muted">{t('promoters.selected', '{{count}} selected', { count: selected.length })}</span>
          <button type="button" className="lm-btn secondary" onClick={() => update('approved')}>{t('promoters.approve', 'Approve')}</button>
          <input type="text" maxLength={128} placeholder={t('promoters.payoutRef', 'Payout reference')} value={payoutRef}
            onChange={(e) => setPayoutRef(e.target.value)} aria-label={t('promoters.payoutRef', 'Payout reference')} />
          <button type="button" className="lm-btn" onClick={() => update('paid')}>{t('promoters.markPaid', 'Mark paid')}</button>
          <button type="button" className="lm-btn secondary" onClick={() => update('void')}>{t('promoters.void', 'Void')}</button>
        </div>
      )}
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows === null ? (
        <LoadingState inline />
      ) : rows.length === 0 ? (
        <div className="note info">{t('promoters.noEarnings', 'No earnings here.')}</div>
      ) : (
        <div className="perf-table-wrap">
          <table className="perf-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" aria-label={t('promoters.selectAll', 'Select all')}
                    checked={actionable.length > 0 && actionable.every((r) => selected.includes(r.id))}
                    onChange={(e) => setSelected(e.target.checked ? actionable.map((r) => r.id) : [])} />
                </th>
                <th>{t('promoters.promoter', 'Promoter')}</th>
                <th>{t('promoters.event', 'Event')}</th>
                <th>{t('promoters.conversion', 'Conversion')}</th>
                <th className="num">{t('promoters.amount', 'Amount')}</th>
                <th>{t('promoters.statusLabel', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {(r.status === 'pending' || r.status === 'approved') && (
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} aria-label={t('promoters.selectRow', 'Select')} />
                    )}
                  </td>
                  <td>{r.promoterName} <span className="lm-badge">{r.promoterCode}</span></td>
                  <td>{r.eventName || '–'}</td>
                  <td>{t(`performance.kinds.${r.kind}`, r.kind)}</td>
                  <td className="num">{formatMoney(r.amount)} {r.currency}</td>
                  <td>
                    <span className={`lm-badge${r.status === 'void' ? ' off' : ''}`}>{t(`promoters.earningStatus.${r.status}`, r.status)}</span>
                    {r.payoutReference && <div className="lm-dest">{r.payoutReference}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
